import React from "react";
import { Link } from "react-router-dom";

// Enhanced Service Page Component with rich content and professional design
export const EnhancedServicePage = ({
  title,
  subtitle,
  heroDescription,
  heroStats,
  whyCareerBuddyPro,
  serviceDetails,
  testimonials,
  ctaSection,
  faqSection,
}) => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-black">
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
                {title}
              </h1>
              {subtitle && (
                <h2 className="text-2xl font-medium text-blue-100 mb-6">
                  {subtitle}
                </h2>
              )}
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                {heroDescription}
              </p>

              <div className="flex flex-wrap gap-6 mb-8">
                <Link to="/talent">
                  <button className="bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                    Find Talent Now
                  </button>
                </Link>

                <Link to="/post-job">
                  <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors">
                    Post a Job
                  </button>
                </Link>
              </div>
            </div>

            {/* <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <div className="grid grid-cols-2 gap-6">
                {heroStats?.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                    <div className="text-blue-100 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Why CareerBuddyPro Section */}
      {whyCareerBuddyPro && (
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose CareerBuddyPro for {title}?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {whyCareerBuddyPro.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {whyCareerBuddyPro.benefits?.map((benefit, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                    <span className="text-2xl">{benefit.icon}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Service Details Section */}
      {serviceDetails && (
        <div className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {serviceDetails.title}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {serviceDetails.description}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {serviceDetails.categories?.map((category, idx) => (
                <div key={idx} className="bg-black rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {category.title}
                  </h3>
                  <div className="space-y-4">
                    {category.services?.map((service, serviceIdx) => (
                      <div
                        key={serviceIdx}
                        className="bg-white rounded-lg p-4 border border-gray-200"
                      >
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1">
                              {service.name}
                            </h4>
                            <p className="text-gray-600 text-sm">
                              {service.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Testimonials Section */}
      {testimonials && (
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Success Stories
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See how businesses have transformed with top{" "}
                {title.toLowerCase()} talent from CareerBuddyPro
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl p-8 shadow-sm border border-gray-100"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-5 h-5 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {testimonial.name}
                      </div>
                      <div className="text-gray-600 text-sm">
                        {testimonial.title}
                      </div>
                      <div className="text-gray-500 text-sm">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      {faqSection && (
        <div className="py-20">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about hiring {title.toLowerCase()}{" "}
                talent
              </p>
            </div>

            <div className="space-y-6">
              {faqSection.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl border border-gray-200 p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      {ctaSection && (
        <div className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              {ctaSection.title}
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              {ctaSection.description}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/talent">
                <button className="bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                  {ctaSection.primaryButton}
                </button>
              </Link>
              {/* {ctaSection.secondaryButton && (
                <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors">
                  {ctaSection.secondaryButton}
                </button>
              )} */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Category/Service Data

export const webDevelopmentData = {
  title: "Web Development",
  subtitle: "Build Modern, Scalable Web Applications",
  heroDescription:
    "Connect with expert web developers who create stunning, high-performance websites and applications. From simple landing pages to complex enterprise solutions, find the perfect developer for your project.",
  heroStats: [
    { value: "51K+", label: "Web Developers" },
    { value: "98%", label: "Project Success Rate" },
    { value: "24h", label: "Average Response Time" },
    { value: "4.9/5", label: "Average Rating" },
  ],
  whyCareerBuddyPro: {
    description:
      "CareerBuddyPro connects you with pre-vetted, top-tier web development talent from around the globe. Our platform ensures quality, security, and seamless collaboration.",
    benefits: [
      {
        icon: "🔍",
        title: "Pre-Vetted Talent",
        description:
          "All developers go through rigorous technical assessments and portfolio reviews. Only the top 5% make it to our platform.",
      },
      {
        icon: "⚡",
        title: "Fast Matching",
        description:
          "Our AI-powered matching system connects you with the right developers in under 24 hours based on your specific requirements.",
      },
      {
        icon: "🛡️",
        title: "Secure & Protected",
        description:
          "Built-in escrow system, NDAs, and IP protection ensure your project and payments are always secure.",
      },
    ],
  },
  serviceDetails: {
    title: "Comprehensive Web Development Services",
    description:
      "From concept to deployment, our developers handle every aspect of web development with modern technologies and best practices.",
    categories: [
      {
        title: "Frontend Development",
        services: [
          {
            name: "React Development",
            description:
              "Modern, responsive applications with React, Next.js, and TypeScript",
          },
          {
            name: "Vue.js Development",
            description:
              "Lightweight, progressive applications with Vue.js ecosystem",
          },
          {
            name: "Angular Development",
            description: "Enterprise-grade applications with Angular and RxJS",
          },
          {
            name: "Responsive Design",
            description: "Mobile-first, cross-browser compatible designs",
          },
          {
            name: "UI/UX Implementation",
            description:
              "Pixel-perfect implementation of designs with smooth animations",
          },
        ],
      },
      {
        title: "Backend Development",
        services: [
          {
            name: "Node.js Development",
            description:
              "Scalable server-side applications with Express and NestJS",
          },
          {
            name: "Python Development",
            description: "Robust applications with Django, Flask, and FastAPI",
          },
          {
            name: "Database Design",
            description: "PostgreSQL, MongoDB, and Redis implementation",
          },
          {
            name: "API Development",
            description:
              "RESTful and GraphQL APIs with comprehensive documentation",
          },
          {
            name: "Cloud Integration",
            description: "AWS, GCP, and Azure deployment and management",
          },
        ],
      },
    ],
  },
  testimonials: [
    {
      quote:
        "The developer we hired through CareerBuddyPro transformed our outdated website into a modern, lightning-fast application. The quality exceeded our expectations.",
      name: "Sarah Chen",
      title: "CTO",
      company: "TechFlow Inc.",
    },
    {
      quote:
        "Finding a skilled React developer was challenging until we used CareerBuddyPro. The matching process was seamless and the developer delivered exceptional work.",
      name: "Michael Rodriguez",
      title: "Product Manager",
      company: "StartupXYZ",
    },
    {
      quote:
        "Our e-commerce platform needed urgent scalability improvements. The full-stack developer we found delivered a complete solution in record time.",
      name: "Emily Johnson",
      title: "Founder",
      company: "BoutiqueCo",
    },
  ],
  faqSection: [
    {
      question: "How do I know if a developer is qualified for my project?",
      answer:
        "All developers on CareerBuddyPro have verified portfolios, technical assessments, and client reviews. You can review their previous work, test projects, and conduct interviews before hiring.",
    },
    {
      question: "What's the average timeline for web development projects?",
      answer:
        "Simple websites take 1-2 weeks, while complex applications can take 2-6 months. Your developer will provide a detailed timeline during the proposal phase.",
    },
    {
      question: "Do developers provide ongoing maintenance?",
      answer:
        "Yes, many developers offer maintenance packages. You can discuss ongoing support needs during the hiring process.",
    },
    {
      question: "How does payment work for web development projects?",
      answer:
        "Payments are held in escrow and released based on milestones you agree upon with the developer. This protects both parties and ensures quality delivery.",
    },
  ],
  ctaSection: {
    title: "Ready to Build Your Next Web Application?",
    description:
      "Join thousands of companies who have found their perfect web development partner on CareerBuddyPro. Start your project today.",
    primaryButton: "Find Web Developers",
    secondaryButton: "Post Your Project",
  },
};

export const mobileAppsData = {
  title: "Mobile App Development",
  subtitle: "Create Native & Cross-Platform Mobile Applications",
  heroDescription:
    "Build iOS and Android applications with experienced mobile developers. From startup MVPs to enterprise apps, our developers create mobile experiences that engage users and drive business growth.",
  heroStats: [
    { value: "25K+", label: "Mobile Developers" },
    { value: "500K+", label: "Apps Launched" },
    { value: "4.8/5", label: "Average Rating" },
    { value: "95%", label: "On-Time Delivery" },
  ],
  whyCareerBuddyPro: {
    description:
      "CareerBuddyPro's mobile developers have shipped apps for Fortune 500 companies and successful startups. Get access to top talent with proven track records.",
    benefits: [
      {
        icon: "📱",
        title: "Native & Cross-Platform",
        description:
          "Expert developers in iOS (Swift), Android (Kotlin), React Native, and Flutter for any mobile strategy.",
      },
      {
        icon: "🚀",
        title: "App Store Success",
        description:
          "Developers who understand App Store guidelines, optimization, and user acquisition strategies.",
      },
      {
        icon: "🔧",
        title: "End-to-End Service",
        description:
          "From ideation to deployment and maintenance, including backend integration and analytics.",
      },
    ],
  },
  serviceDetails: {
    title: "Complete Mobile Development Solutions",
    description:
      "Our mobile developers handle everything from UI design to backend integration, ensuring your app delivers exceptional user experiences.",
    categories: [
      {
        title: "Native Development",
        services: [
          {
            name: "iOS Development",
            description:
              "Native Swift and SwiftUI applications optimized for iPhone and iPad",
          },
          {
            name: "Android Development",
            description:
              "Kotlin and Java applications following Material Design principles",
          },
          {
            name: "watchOS & WearOS",
            description:
              "Companion apps for Apple Watch and Android Wear devices",
          },
          {
            name: "App Store Optimization",
            description: "Optimization for better visibility and downloads",
          },
          {
            name: "Performance Optimization",
            description:
              "Memory management and battery efficiency optimization",
          },
        ],
      },
      {
        title: "Cross-Platform Solutions",
        services: [
          {
            name: "React Native",
            description:
              "Shared codebase for iOS and Android with native performance",
          },
          {
            name: "Flutter Development",
            description:
              "Google's UI framework for beautiful, fast applications",
          },
          {
            name: "Xamarin Development",
            description: "Microsoft's platform for enterprise mobile solutions",
          },
          {
            name: "Progressive Web Apps",
            description: "Web apps with native-like mobile experiences",
          },
          {
            name: "Hybrid Solutions",
            description: "Cordova and Ionic for rapid development",
          },
        ],
      },
    ],
  },
  testimonials: [
    {
      quote:
        "Our food delivery app was built by an amazing React Native developer from CareerBuddyPro. The app handles thousands of daily orders flawlessly.",
      name: "David Park",
      title: "CEO",
      company: "QuickBite",
    },
    {
      quote:
        "The iOS developer we hired created a beautiful fitness app that's now ranked #3 in Health & Fitness. Couldn't be happier with the result.",
      name: "Lisa Thompson",
      title: "Founder",
      company: "FitLife",
    },
    {
      quote:
        "Working with a Flutter expert from CareerBuddyPro, we launched our fintech app on both platforms simultaneously. The code quality was exceptional.",
      name: "James Wilson",
      title: "CTO",
      company: "PayFlow",
    },
  ],
  faqSection: [
    {
      question: "Should I choose native or cross-platform development?",
      answer:
        "Native offers the best performance and platform-specific features, while cross-platform reduces development time and cost. Our developers can help you choose based on your requirements.",
    },
    {
      question: "How long does it take to develop a mobile app?",
      answer:
        "Simple apps take 2-4 months, while complex apps with backend integration can take 6-12 months. Timeline depends on features, platforms, and complexity.",
    },
    {
      question: "Do developers help with app store submission?",
      answer:
        "Yes, experienced developers assist with app store guidelines, submission process, and optimization for better visibility and approval rates.",
    },
    {
      question: "What about app maintenance and updates?",
      answer:
        "Most developers offer ongoing maintenance services including bug fixes, OS updates, new features, and performance monitoring.",
    },
  ],
  ctaSection: {
    title: "Turn Your App Idea Into Reality",
    description:
      "Connect with mobile developers who have launched successful apps. Start building your mobile presence today.",
    primaryButton: "Find Mobile Developers",
    secondaryButton: "Post Your App Project",
  },
};

export const aiDevelopmentData = {
  title: "AI & Machine Learning Development",
  subtitle: "Harness the Power of Artificial Intelligence",
  heroDescription:
    "Build intelligent applications with AI/ML experts who specialize in machine learning, natural language processing, computer vision, and data science. Transform your business with cutting-edge AI solutions.",
  heroStats: [
    { value: "12K+", label: "AI Specialists" },
    { value: "89%", label: "Model Accuracy Improvement" },
    { value: "200+", label: "AI Projects Delivered" },
    { value: "4.9/5", label: "Client Satisfaction" },
  ],
  whyCareerBuddyPro: {
    description:
      "CareerBuddyPro's AI developers have experience with Fortune 500 AI initiatives and cutting-edge research. Access talent trained on the latest AI technologies and frameworks.",
    benefits: [
      {
        icon: "🧠",
        title: "Latest AI Technologies",
        description:
          "Experts in GPT, BERT, TensorFlow, PyTorch, and emerging AI frameworks and models.",
      },
      {
        icon: "📊",
        title: "Data Science Expertise",
        description:
          "Full-stack data scientists who handle data collection, processing, modeling, and deployment.",
      },
      {
        icon: "🎯",
        title: "Business-Focused AI",
        description:
          "Developers who understand how to apply AI to solve real business problems and drive ROI.",
      },
    ],
  },
  serviceDetails: {
    title: "Comprehensive AI Development Services",
    description:
      "From proof of concept to production deployment, our AI experts deliver intelligent solutions that drive business value.",
    categories: [
      {
        title: "Machine Learning Solutions",
        services: [
          {
            name: "Custom ML Models",
            description:
              "Predictive models, recommendation systems, and classification algorithms",
          },
          {
            name: "Deep Learning",
            description:
              "Neural networks for complex pattern recognition and predictions",
          },
          {
            name: "AutoML Solutions",
            description:
              "Automated machine learning for rapid model development",
          },
          {
            name: "Model Optimization",
            description:
              "Performance tuning and deployment optimization for production",
          },
          {
            name: "MLOps Implementation",
            description: "End-to-end ML pipeline automation and monitoring",
          },
        ],
      },
      {
        title: "AI Applications",
        services: [
          {
            name: "Natural Language Processing",
            description:
              "Chatbots, sentiment analysis, and text processing systems",
          },
          {
            name: "Computer Vision",
            description:
              "Image recognition, object detection, and visual AI applications",
          },
          {
            name: "Voice AI",
            description:
              "Speech recognition, synthesis, and voice-powered interfaces",
          },
          {
            name: "Generative AI",
            description:
              "Content generation, image synthesis, and creative AI applications",
          },
          {
            name: "AI Integration",
            description:
              "Integrating AI capabilities into existing applications and workflows",
          },
        ],
      },
    ],
  },
  testimonials: [
    {
      quote:
        "The AI developer we hired built a recommendation system that increased our customer engagement by 40%. The expertise was exactly what we needed.",
      name: "Rachel Adams",
      title: "VP of Product",
      company: "RetailTech",
    },
    {
      quote:
        "Our computer vision project for quality control was delivered perfectly. The model accuracy exceeded our requirements and saved us significant costs.",
      name: "Mark Stevens",
      title: "Operations Director",
      company: "Manufacturing Plus",
    },
    {
      quote:
        "The NLP solution for our customer support chatbot handles 80% of inquiries automatically. It's been a game-changer for our team.",
      name: "Anna Kumar",
      title: "Customer Success Manager",
      company: "SupportFlow",
    },
  ],
  faqSection: [
    {
      question: "What data do I need for an AI project?",
      answer:
        "Data requirements vary by project type. Our AI experts can help assess your data quality, suggest collection strategies, and work with existing datasets to build effective models.",
    },
    {
      question: "How long does it take to develop an AI solution?",
      answer:
        "Simple AI integrations take 2-4 weeks, while custom ML models can take 2-6 months. Timeline depends on data availability, model complexity, and integration requirements.",
    },
    {
      question: "Do AI developers provide ongoing model maintenance?",
      answer:
        "Yes, AI models require regular monitoring and updates. Many developers offer maintenance services including model retraining, performance monitoring, and improvements.",
    },
    {
      question: "What's the difference between AI consulting and development?",
      answer:
        "AI consulting helps define strategy and feasibility, while development involves building and implementing AI solutions. Many experts offer both services.",
    },
  ],
  ctaSection: {
    title: "Ready to Implement AI in Your Business?",
    description:
      "Connect with AI experts who can transform your ideas into intelligent solutions. Start your AI journey today.",
    primaryButton: "Find AI Developers",
    secondaryButton: "Get AI Consultation",
  },
};

export const marketingAutomationData = {
  title: "Marketing Automation",
  subtitle: "Streamline Your Marketing with Automation Experts",
  heroDescription:
    "Scale your marketing efforts with automation specialists who create systems that nurture leads, engage customers, and drive conversions. From email campaigns to omnichannel strategies, automate your path to growth.",
  heroStats: [
    { value: "15K+", label: "Marketing Experts" },
    { value: "300%", label: "Average ROI Increase" },
    { value: "85%", label: "Lead Generation Improvement" },
    { value: "24/7", label: "Automation Support" },
  ],
  whyCareerBuddyPro: {
    description:
      "CareerBuddyPro's marketing automation experts have increased revenue for thousands of businesses. Access specialists who understand the latest tools and strategies.",
    benefits: [
      {
        icon: "🎯",
        title: "Strategy-First Approach",
        description:
          "Experts who design automation workflows based on your customer journey and business goals.",
      },
      {
        icon: "🔧",
        title: "Platform Expertise",
        description:
          "Certified specialists in HubSpot, Marketo, Pardot, Mailchimp, and all major automation platforms.",
      },
      {
        icon: "📈",
        title: "Growth-Focused",
        description:
          "Proven track record of increasing conversion rates, reducing costs, and scaling marketing operations.",
      },
    ],
  },
  serviceDetails: {
    title: "Complete Marketing Automation Solutions",
    description:
      "Our automation experts design, implement, and optimize marketing systems that work around the clock to grow your business.",
    categories: [
      {
        title: "Email Marketing Automation",
        services: [
          {
            name: "Drip Campaigns",
            description:
              "Automated email sequences that nurture leads and drive conversions",
          },
          {
            name: "Behavioral Triggers",
            description:
              "Dynamic emails based on user actions and engagement patterns",
          },
          {
            name: "Segmentation Strategy",
            description:
              "Advanced audience segmentation for personalized messaging",
          },
          {
            name: "A/B Testing",
            description:
              "Continuous optimization of subject lines, content, and timing",
          },
          {
            name: "Email Deliverability",
            description: "Ensuring high inbox placement and sender reputation",
          },
        ],
      },
      {
        title: "Omnichannel Automation",
        services: [
          {
            name: "Social Media Automation",
            description:
              "Automated posting, engagement, and social listening systems",
          },
          {
            name: "Lead Scoring",
            description:
              "Automated systems to identify and prioritize high-quality leads",
          },
          {
            name: "CRM Integration",
            description:
              "Seamless data flow between marketing and sales systems",
          },
          {
            name: "Content Personalization",
            description:
              "Dynamic content delivery based on user behavior and preferences",
          },
          {
            name: "Analytics & Reporting",
            description:
              "Automated reporting and performance tracking dashboards",
          },
        ],
      },
    ],
  },
  testimonials: [
    {
      quote:
        "The marketing automation specialist we found through CareerBuddyPro increased our email open rates by 45% and conversion rates by 60%. Outstanding results!",
      name: "Jennifer Lee",
      title: "Marketing Director",
      company: "GrowthCorp",
    },
    {
      quote:
        "Our lead nurturing was completely transformed. The automation workflows now generate 3x more qualified leads with less manual effort from our team.",
      name: "Robert Martinez",
      title: "VP of Sales",
      company: "B2B Solutions",
    },
    {
      quote:
        "The HubSpot automation setup was flawless. We now have a fully integrated marketing and sales funnel that runs itself while we focus on strategy.",
      name: "Sophie Chang",
      title: "CMO",
      company: "TechStartup",
    },
  ],
  faqSection: [
    {
      question: "Which marketing automation platform should I use?",
      answer:
        "The choice depends on your business size, budget, and needs. Our experts can recommend the best platform (HubSpot, Marketo, Pardot, etc.) based on your requirements.",
    },
    {
      question: "How quickly can I see results from marketing automation?",
      answer:
        "Initial setup takes 2-6 weeks, with results often visible within the first month. Full optimization and maximum ROI typically occur within 3-6 months.",
    },
    {
      question: "Do I need a large email list to start automation?",
      answer:
        "No, automation can help grow your list while nurturing existing contacts. Even small lists benefit from automated workflows and personalized messaging.",
    },
    {
      question: "What's included in a marketing automation setup?",
      answer:
        "Typically includes strategy planning, platform setup, workflow creation, email templates, lead scoring, integrations, testing, and training for your team.",
    },
  ],
  ctaSection: {
    title: "Automate Your Way to Marketing Success",
    description:
      "Stop doing repetitive marketing tasks manually. Find automation experts who can scale your marketing while you focus on strategy.",
    primaryButton: "Find Marketing Experts",
    secondaryButton: "Get Automation Quote",
  },
};

export const technicalConsultingData = {
  title: "Technical Consulting",
  subtitle: "Expert Guidance for Your Technology Strategy",
  heroDescription:
    "Get strategic technical advice from senior consultants who have architected solutions for leading companies. Whether you need architecture reviews, technology selection, or digital transformation guidance, our experts provide the insights you need.",
  heroStats: [
    { value: "8K+", label: "Senior Consultants" },
    { value: "15+", label: "Years Avg Experience" },
    { value: "500+", label: "Companies Advised" },
    { value: "92%", label: "Implementation Success Rate" },
  ],
  whyCareerBuddyPro: {
    description:
      "CareerBuddyPro's technical consultants are former CTOs, architects, and senior engineers from top tech companies. Get access to strategic thinking that drives results.",
    benefits: [
      {
        icon: "🏗️",
        title: "Architecture Expertise",
        description:
          "Consultants who have designed systems handling millions of users and complex business requirements.",
      },
      {
        icon: "💡",
        title: "Strategic Thinking",
        description:
          "Beyond technical solutions, get business-aligned technology strategies that drive growth.",
      },
      {
        icon: "⚡",
        title: "Rapid Assessment",
        description:
          "Quick identification of issues, opportunities, and actionable recommendations for immediate impact.",
      },
    ],
  },
  serviceDetails: {
    title: "Comprehensive Technical Consulting Services",
    description:
      "Our consultants provide strategic guidance across all aspects of technology, from architecture design to team optimization.",
    categories: [
      {
        title: "System Architecture & Design",
        services: [
          {
            name: "Architecture Reviews",
            description:
              "Comprehensive analysis of existing systems for scalability and performance",
          },
          {
            name: "System Design",
            description:
              "Design of new systems and applications from ground up",
          },
          {
            name: "Scalability Planning",
            description: "Strategies for handling growth and increased load",
          },
          {
            name: "Migration Strategies",
            description:
              "Safe migration paths for legacy systems and cloud adoption",
          },
          {
            name: "Security Audits",
            description:
              "Comprehensive security assessments and recommendations",
          },
        ],
      },
      {
        title: "Technology Strategy & Leadership",
        services: [
          {
            name: "Technology Roadmapping",
            description:
              "Strategic planning for technology initiatives and investments",
          },
          {
            name: "Team Optimization",
            description:
              "Improving development processes, team structure, and productivity",
          },
          {
            name: "Vendor Evaluation",
            description:
              "Assessment of technology vendors and build vs buy decisions",
          },
          {
            name: "Digital Transformation",
            description:
              "Comprehensive digital transformation strategy and execution",
          },
          {
            name: "Technical Due Diligence",
            description:
              "Technology assessments for mergers, acquisitions, and investments",
          },
        ],
      },
    ],
  },
  testimonials: [
    {
      quote:
        "The technical consultant we hired through CareerBuddyPro identified critical scalability issues before they became problems. Their recommendations saved us months of downtime.",
      name: "Alex Thompson",
      title: "CTO",
      company: "ScaleUp Inc",
    },
    {
      quote:
        "Our cloud migration strategy was completely transformed by their expertise. We reduced costs by 40% while improving performance and security.",
      name: "Maria Santos",
      title: "VP of Engineering",
      company: "CloudFirst",
    },
    {
      quote:
        "The technology roadmap they created aligned our entire engineering organization. We now have clear priorities and measurable progress toward our goals.",
      name: "Daniel Kim",
      title: "CEO",
      company: "TechVision",
    },
  ],
  faqSection: [
    {
      question: "When should I hire a technical consultant?",
      answer:
        "Consider consulting when facing scaling challenges, making major technology decisions, experiencing performance issues, or needing strategic guidance for digital transformation.",
    },
    {
      question: "How long do technical consulting engagements last?",
      answer:
        "Engagements range from 1-week assessments to 6-month strategic initiatives. Most architecture reviews take 2-4 weeks, while transformation projects can last 3-12 months.",
    },
    {
      question: "What deliverables can I expect?",
      answer:
        "Typical deliverables include technical assessments, architecture diagrams, roadmaps, implementation plans, best practices documentation, and team training materials.",
    },
    {
      question: "Do consultants work with our existing team?",
      answer:
        "Yes, consultants typically work closely with your internal teams, providing knowledge transfer, mentoring, and ensuring recommendations are implementable by your team.",
    },
  ],
  ctaSection: {
    title: "Get Expert Technical Guidance Today",
    description:
      "Don't let technical challenges slow down your business. Connect with senior consultants who can provide the strategic direction you need.",
    primaryButton: "Find Technical Consultants",
    secondaryButton: "Schedule Consultation",
  },
};

export default {
  EnhancedServicePage,
  webDevelopmentData,
  mobileAppsData,
  aiDevelopmentData,
  marketingAutomationData,
  technicalConsultingData,
};
